import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register the plugin
gsap.registerPlugin(ScrollTrigger);

// Define prop types
type JumpAnimationConfig = {
  triggerSelector: string;
  itemSelector: string;
  startMobile?: string;
  startDesktop?: string;
  bounceEase?: boolean;
  mobileBreakpoint?: number;
};

export const useJumpAnimation = (config: JumpAnimationConfig) => {
  const {
    triggerSelector,
    itemSelector,
    startMobile = 'top center+=200',
    startDesktop = 'top center+=300',
    bounceEase = true,
    mobileBreakpoint = 1023,
  } = config;

  useEffect(() => {
    const triggerEl = document.querySelector(triggerSelector);
    if (!triggerEl) return;

    // Set initial animation state
    gsap.set(itemSelector, {
      y: -100,
      opacity: 0,
    });

    const easeType = bounceEase ? 'bounce' : 'power2.out';
    const isMobile = window.innerWidth < mobileBreakpoint;

    if (isMobile) {
      const items = gsap.utils.toArray(itemSelector) as HTMLElement[];

      items.forEach((item) => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: startMobile,
          },
        });

        tl.to(item, {
          y: 0,
          opacity: 1,
          ease: easeType,
          duration: 1.5,
        });
      });
    } else {
      gsap.to(itemSelector, {
        scrollTrigger: {
          trigger: triggerSelector,
          start: startDesktop,
        },
        y: 0,
        opacity: 1,
        ease: easeType,
        duration: 1.5,
        stagger: {
          each: 0.3,
        },
      });
    }

    // Cleanup: Kill all ScrollTrigger instances on unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [
    triggerSelector,
    itemSelector,
    startMobile,
    startDesktop,
    bounceEase,
    mobileBreakpoint,
  ]);
};
